import { X } from "lucide-react";
import { FeedbackBanner } from "@/core/components/feedback-banner";
import { Button } from "@/core/components/ui/button";
import { Stack } from "@/core/components/ui/stack";
import { Typography } from "@/core/components/ui/typography";
import { cn } from "@/core/utils/utils";
import { DeliveryHistory } from "./delivery-history";
import { NoteComposer } from "./note-composer";
import type { PollingResult } from "./polling";
import { SIDEBAR_VIEW, type SidebarView } from "./sidebar-view";
import { SidebarViewToggle } from "./sidebar-view-toggle";
import { StatementPreview } from "./statement-preview";
import type {
  PayrollStatementNotificationRecipient,
  PayrollStatementProjectionBuild,
} from "./types";
import { getNotificationReasonLabel } from "./utils";

export const TechnicianDetailSidebar = ({
  businessId,
  periodStart,
  recipient,
  view,
  onViewChange,
  onClose,
  noteDraft,
  onNoteChange,
  previewOnly,
  projectionBuild,
}: {
  businessId: string | null;
  periodStart: string | null;
  recipient: PayrollStatementNotificationRecipient | null;
  view: SidebarView;
  onViewChange: (view: SidebarView) => void;
  onClose: () => void;
  noteDraft: string;
  onNoteChange: (value: string) => void;
  previewOnly: boolean;
  projectionBuild: PollingResult<PayrollStatementProjectionBuild> | null;
}) => {
  if (!recipient) return null;

  const ineligibleReason = getNotificationReasonLabel(
    recipient.ineligible_reason,
  );

  return (
    <Stack
      fullWidth
      className={cn("h-full min-h-0", { "opacity-60": previewOnly })}
      gap="none"
    >
      <Stack
        direction="row"
        align="center"
        justify="between"
        fullWidth
        gap="md"
        className="shrink-0 border-b border-stroke-tertiary px-6 py-4"
      >
        <SidebarViewToggle view={view} onViewChange={onViewChange} />
        <Button variant="icon" size="icon" onClick={onClose} aria-label="Close">
          <X className="h-4 w-4" />
        </Button>
      </Stack>

      <div className="min-h-0 w-full flex-1 overflow-y-auto">
        {view === SIDEBAR_VIEW.history ? (
          <div className="pt-4">
            {!recipient.eligible && ineligibleReason && (
              <div className="px-6 pb-2">
                <FeedbackBanner variant="warning">
                  <Typography variant="bodyW3">
                    This technician can't be emailed yet.
                  </Typography>
                  <Typography variant="bodyW2" textColor="secondary">
                    {ineligibleReason}
                  </Typography>
                </FeedbackBanner>
              </div>
            )}
            {recipient.eligible && (
              <Stack gap="xs" fullWidth className="px-6 pb-5">
                <Typography
                  variant="captionW1"
                  className="uppercase tracking-wide text-tertiary-text"
                >
                  Note for the next send (optional)
                </Typography>
                <NoteComposer
                  businessId={businessId}
                  periodStart={periodStart}
                  technicianId={recipient.technician_id}
                  value={noteDraft}
                  onChange={onNoteChange}
                  placeholder={`Hey ${recipient.name.split(" ")[0]}, here's what changed...`}
                  disabled={previewOnly}
                />
              </Stack>
            )}
            <DeliveryHistory deliveries={recipient.delivery_history} />
          </div>
        ) : (
          <StatementPreview
            recipient={recipient}
            projectionBuild={projectionBuild}
          />
        )}
      </div>
    </Stack>
  );
};
